import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { EventRegistration, Order } from '../types';
import { registrationApi } from '../api/RegistrationApi';
import { useUpdateOrderCache, useOrder, useInvalidateOrder } from '../hooks/useOrder';
import { useFlash } from './FlashContext';
import { StructuredApiError } from '@portal/api/apiErrors';
import { ConfirmationModal } from '../components/ConfirmationModal';

interface MultiRegistrationContextType {
    registrations: EventRegistration[];
    currentRegistrationId: string | null;
    isAddingRegistration: boolean;
    isSwitchingRegistration: boolean;
    isRemovingRegistration: boolean;
    switchToRegistration: (registrationId: string) => Promise<void>;
    addRegistration: () => Promise<void>;
    removeRegistration: (registrationId: string) => void;
    updateRegistrationProgress: (registrationId: string, step: string) => Promise<void>;
    getNextGuestNeedingSurvey: () => EventRegistration | null;
    getPrimaryRegistration: () => EventRegistration | null;
    getGuestRegistrations: () => EventRegistration[];
}

const MultiRegistrationContext = createContext<MultiRegistrationContextType | null>(null);

interface MultiRegistrationProviderProps {
    children: ReactNode;
    order?: Order;
    orderToken: string;
}

export const MultiRegistrationProvider: React.FC<MultiRegistrationProviderProps> = ({ 
    children, 
    order: initialOrder,
    orderToken 
}) => {
    const updateOrderCache = useUpdateOrderCache();
    const invalidateOrder = useInvalidateOrder();
    const { setFlash } = useFlash();
    
    // Use React Query to get the live order data
    const { data: order } = useOrder(orderToken);
    const currentOrder = order || initialOrder;
    
    const [isAddingRegistration, setIsAddingRegistration] = useState(false);
    const [isSwitchingRegistration, setIsSwitchingRegistration] = useState(false);
    const [isRemovingRegistration, setIsRemovingRegistration] = useState(false);
    const [confirmRemoveModal, setConfirmRemoveModal] = useState<{
        isOpen: boolean;
        registrationId: string | null;
    }>({ isOpen: false, registrationId: null });
    
    const registrations = currentOrder?.eventRegistrations || [];
    const currentRegistrationId = currentOrder?.pendingRegistration?.id || null;
    
    const switchToRegistration = useCallback(async (registrationId: string) => {
        // Find the target registration
        const targetRegistration = registrations.find(r => r.id.toString() === registrationId);
        
        // Check if we're trying to switch to a guest registration
        if (targetRegistration && (targetRegistration.isGuest || targetRegistration.is_guest)) {
            // Check if primary registration exists and is incomplete
            const primaryReg = registrations.find(r => !r.isGuest && !r.is_guest);
            if (primaryReg && (!primaryReg.participants || primaryReg.participants.length === 0)) {
                setFlash({ 
                    message: 'Please complete the primary registration information before proceeding to guest registration.', 
                    type: 'error' 
                });
                return;
            }
        }
        
        setIsSwitchingRegistration(true);
        try {
            const updatedOrder = await registrationApi.switchToRegistration(orderToken, registrationId);
            updateOrderCache(orderToken, updatedOrder);
        } catch (error) {
            if (error instanceof StructuredApiError) {
                setFlash({ 
                    message: error.message || 'Failed to switch registration', 
                    type: 'error' 
                });
            }
            throw error;
        } finally {
            setIsSwitchingRegistration(false);
        }
    }, [orderToken, updateOrderCache, setFlash, registrations]);
    
    const addRegistration = useCallback(async () => {
        setIsAddingRegistration(true);
        try {
            const updatedOrder = await registrationApi.addAnotherRegistration(orderToken);
            updateOrderCache(orderToken, updatedOrder);
            
            // Also invalidate to ensure all components get the update
            invalidateOrder(orderToken);
            
            setFlash({ 
                message: 'New participant added. Please select their participant type.', 
                type: 'success' 
            });
        } catch (error) {
            if (error instanceof StructuredApiError) {
                setFlash({ 
                    message: error.message || 'Failed to add another participant', 
                    type: 'error' 
                });
            }
            throw error;
        } finally {
            setIsAddingRegistration(false);
        }
    }, [orderToken, updateOrderCache, invalidateOrder, setFlash]);
    
    const removeRegistration = useCallback((registrationId: string) => {
        setConfirmRemoveModal({ isOpen: true, registrationId });
    }, []);
    
    const handleConfirmRemove = useCallback(async () => {
        const registrationId = confirmRemoveModal.registrationId;
        if (!registrationId) return;
        
        setIsRemovingRegistration(true);
        try {
            const updatedOrder = await registrationApi.removeRegistration(orderToken, registrationId);
            updateOrderCache(orderToken, updatedOrder);
            
            // Also invalidate to ensure all components get the update
            invalidateOrder(orderToken);
            
            setFlash({ 
                message: 'Registration removed successfully', 
                type: 'success' 
            });
            setConfirmRemoveModal({ isOpen: false, registrationId: null });
        } catch (error) {
            if (error instanceof StructuredApiError) {
                setFlash({ 
                    message: error.message || 'Failed to remove registration', 
                    type: 'error' 
                });
            }
            // Don't throw the error to keep the modal open for retry
        } finally {
            setIsRemovingRegistration(false);
        }
    }, [confirmRemoveModal.registrationId, orderToken, updateOrderCache, invalidateOrder, setFlash]);
    
    const updateRegistrationProgress = useCallback(async (registrationId: string, step: string) => {
        try {
            await registrationApi.updateRegistrationProgress(orderToken, registrationId, step);
            // Note: We don't update the order cache here as the progress update
            // doesn't return a full order object
        } catch (error) {
            // Silently fail progress updates to not interrupt user flow
        }
    }, [orderToken]);
    
    const getPrimaryRegistration = useCallback(() => {
        return registrations.find(r => !r.isGuest && !r.is_guest) || null;
    }, [registrations]);
    
    const getGuestRegistrations = useCallback(() => {
        return registrations.filter(r => r.isGuest || r.is_guest)
            .sort((a, b) => (a.guestNumber || 0) - (b.guestNumber || 0));
    }, [registrations]);
    
    const getNextGuestNeedingSurvey = useCallback(() => {
        const guestRegs = getGuestRegistrations();
        
        // Find the first guest registration without participants (meaning no survey completed)
        return guestRegs.find(reg => !reg.participants || reg.participants.length === 0) || null;
    }, [getGuestRegistrations]);
    
    return (
        <>
            <MultiRegistrationContext.Provider value={{
                registrations,
                currentRegistrationId,
                isAddingRegistration,
                isSwitchingRegistration,
                isRemovingRegistration,
                switchToRegistration,
                addRegistration,
                removeRegistration,
                updateRegistrationProgress,
                getNextGuestNeedingSurvey,
                getPrimaryRegistration,
                getGuestRegistrations
            }}>
                {children}
            </MultiRegistrationContext.Provider>
            
            <ConfirmationModal
                isOpen={confirmRemoveModal.isOpen}
                onClose={() => setConfirmRemoveModal({ isOpen: false, registrationId: null })}
                onConfirm={handleConfirmRemove}
                title="Remove Registration"
                message="Are you sure you want to remove this registration? This action cannot be undone."
                confirmText="Remove"
                cancelText="Cancel"
                confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
                isLoading={isRemovingRegistration}
            />
        </>
    );
};

// Hook to use the multi-registration context
export const useMultiRegistration = () => {
    const context = useContext(MultiRegistrationContext);
    if (!context) {
        throw new Error('useMultiRegistration must be used within a MultiRegistrationProvider');
    }
    return context;
};

// Export the context for testing purposes
export { MultiRegistrationContext };